/* eslint-disable no-restricted-syntax -- §8/§14.2 wants user-visible Slovak to go through `<T>`, and
   these do: `SidebarSection` renders every item as `<T>{item.label}</T>`, the same way it renders
   legacy's. They are *source strings for the translator*, which is exactly what `NavItem.label` is
   documented as in `generated/nav-tree.ts` - and that file holds sixty-two of them and passes only
   because `src/generated/**` is outside lint. This one is hand-written, so it says so instead. */
import { Bell, Languages, Receipt, Settings2, Store, Truck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { RouteGuard } from '../module-contract'

/**
 * The sidebar entries for pages **legacy does not have** — `FRONTEND.md` §9.30.
 *
 * `generated/nav-tree.ts` is *"the sidebar exactly as legacy composes it"*, generated from
 * `docs/INVENTORY/manager-nav.json`. Everything §9.5 added to the backend and gave a screen —
 * surcharges, VAT categories, sales channels, the *na sekeru* tabs — is by definition absent from
 * legacy's nav, so the generated file cannot mention it and the pages had **no way in**. The owner
 * found the surcharges one the way anybody would: *"Príplatky a Zálohy kompletne chýbajú"*.
 *
 * They are declared here rather than in the generated file because that file is the parity record and
 * is regenerated; a hand-edit would be overwritten or would misreport what legacy's sidebar contains.
 * Keeping them apart also keeps the two answerable separately — *what did legacy have* and *what did
 * we add* — while being one sidebar on screen.
 *
 * **The guards are the routes' own.** Each one is copied from the module registry entry it points at,
 * so the sidebar cannot offer a page the router then refuses — the §9.7 rule, and the reason
 * `canAccess` takes a `RouteGuard` as readily as a `NavGuard`.
 */
export interface AgosNavItem {
  label: string
  path: string
  icon: LucideIcon
  guard: RouteGuard
}

export interface AgosNavSection {
  heading: string
  items: AgosNavItem[]
  /**
   * Whether the heading is a button that folds the section away.
   *
   * **Absent, this section could never close.** `SidebarSection` reads `section.collapsible !== true`
   * as "always open" and renders the heading as a `<p>` rather than a `<button>`, so the four items
   * below sat open for ever with nothing to click — which is what the owner reported on 2026-08-11:
   * *"Predaj a účtovanie category is not clickable, so these 4 tabs are always visible"*.
   *
   * Six of legacy's nine sections are collapsible and three are not; this one has no legacy
   * counterpart to be faithful to, so it follows the majority rather than inheriting a default
   * nobody chose.
   */
  collapsible: boolean
}

export const AGOS_NAV_SECTIONS: AgosNavSection[] = [
  {
    /**
     * §9.208 — the two hubs, in the section their tabs came from.
     *
     * The heading matches a generated section, so the shell appends these rather than drawing a
     * second block with the same name — and `generate-nav-tree.mjs` drops the seven items they fold
     * up, so *Systémové nastavenia* goes from fifteen entries to ten.
     *
     * **The guards are the routes' own**, which is this file's rule: `anyPermission` for the five
     * settings screens because three of those flags are held by the waiter and one is not, and the
     * translations pair share `translations_admin`.
     */
    heading: 'Systémové nastavenia',
    collapsible: true,
    items: [
      {
        label: 'Nastavenia',
        path: '/settings',
        icon: Settings2,
        guard: {
          systemRoles: ['main_admin', 'admin'],
          anyPermission: [
            'settings_general',
            'settings_sms',
            'settings_email',
            'settings_notifications',
            'settings_monitoring',
          ],
        },
      },
      {
        label: 'Preklady',
        path: '/translations',
        icon: Languages,
        guard: { permission: 'translations_admin' },
      },
      {
        // §9.308, owner item A6. The guard is the three folded routes' own — all three are exactly
        // `{main_admin, admin}`, so unlike *Nastavenia* there is no `anyPermission` to write.
        label: 'Predaj a účtovanie',
        path: '/sales-settings',
        icon: Store,
        guard: { systemRoles: ['main_admin', 'admin'] },
      },
    ],
  },
  {
    /**
     * §9.112 — the owner's point 19. This heading **matches a generated section**, so the shell
     * appends these items to legacy's *Správa rozvoz* rather than drawing a second block with the
     * same name. The driver app used to be reachable only through a top-bar surface switcher, which
     * has gone: *"ja nastavím účtom čo sú rozvozári, že zobrazovať v právach"* — who sees it is the
     * `driver` permission, which is how every other item in this section is already gated.
     */
    heading: 'Správa rozvoz',
    collapsible: true,
    items: [
      {
        label: 'Aplikácia pre šoféra',
        path: '/driving',
        icon: Truck,
        guard: { permission: 'driver', orSystemRoles: ['driver'] },
      },
    ],
  },
  {
    /**
     * §9.292 - the owner placed this one: *"účty na sekeru, if its enabled AFTER Mapovanie stolov -
     * in section Správa reštaurácie."*
     *
     * The heading matches a generated section, so `AppLayout` appends this item to it rather than
     * drawing a second block with the same name - and the last item the generator puts there is
     * *Mapovanie stolov*, so "after" holds by construction rather than by a sort key.
     */
    heading: 'Správa Reštaurácie',
    collapsible: false,
    items: [
      {
        label: 'Účty na sekeru',
        path: '/order-tabs',
        icon: Receipt,
        // `OrderTabController` admits a waiter, and this mirrors it — a waiter runs the tabs.
        // §9.281, owner O20 — and it goes when the channel it exists for is switched off. The route
        // still answers: there are 83 open tabs on that channel and hiding a menu entry is not the
        // same as making money unreachable.
        guard: { systemRoles: ['main_admin', 'admin', 'waiter'], salesChannel: 'na_sekeru' },
      },
    ],
  },
  // §9.308, owner item A6 — *"Predaj a účtovanie: also to be moved somewhere sensible"*. The three
  // items that were here are now tabs of `/sales-settings`, and the entry sits in *Systémové
  // nastavenia* above, beside the other two configuration hubs. The whole heading is gone, which is
  // the same simplification A1 measured as fifteen entries becoming ten.
  //
  // **It was banked, and re-reading found the reason had expired.** The note said the section mixed
  // configuration with *Účty na sekeru*, which a waiter runs daily — and §9.281 had already moved
  // that screen into *Správa Reštaurácie*. FB-482 records that a banked question is a statement
  // about the day it was banked.
  {
    // §9.304, FB-474 - the staff inbox had no way in at all: no entry here, none in legacy's
    // inventory, no hub tab, no link, no bell. It was reachable only by typing the path, and four
    // backend paths write to it - low stock, a recurring invoice, an auto-drafted purchase order.
    //
    // *Správa firmy* because that is where `/reports`, `/invoices` and `/reorder-drafts` already
    // live, which are the subjects it announces. That section exists in the generated tree, so this
    // entry is merged into it by heading rather than creating a second one.
    heading: 'Správa firmy',
    collapsible: true,
    items: [
      {
        label: 'Upozornenia',
        path: '/notifications',
        icon: Bell,
        // The route's own guard. The sidebar may not offer what the router refuses - §9.7, FB-001.
        guard: { systemRoles: ['main_admin', 'admin'] },
      },
    ],
  },
]
